import { ConnectionListResult, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionDownstreamFlowsFilteredByTableQuery,
  VirtualConnectionDownstreamFlowsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamFlowsConnection_nodes,
  VirtualConnectionDownstreamFlowsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamFlowsFilteredByTableQuery';
import {
  VirtualConnectionDownstreamFlowsQuery,
  VirtualConnectionDownstreamFlowsQuery_virtualConnections_downstreamFlowsConnection_nodes,
  VirtualConnectionDownstreamFlowsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamFlowsQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  VirtualConnectionDownstreamFlowsFilteredByTableQueryGql,
} from '../../queries/VirtualConnectionDownstreamFlowsFilteredByTableQueryGql';
import { VirtualConnectionDownstreamFlowsQueryGql } from '../../queries/VirtualConnectionDownstreamFlowsQueryGql';
import { FlowsDisplayGrid, FlowsDisplayGridRowData, getRowDataForFlow } from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = VirtualConnectionDownstreamFlowsQueryVariables;
type QueryResults = VirtualConnectionDownstreamFlowsQuery;
type QueryResultListNode = VirtualConnectionDownstreamFlowsQuery_virtualConnections_downstreamFlowsConnection_nodes;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionDownstreamFlowsFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionDownstreamFlowsFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionDownstreamFlowsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamFlowsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  return virtualConnection ? virtualConnection.downstreamFlowsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const table = virtualConnectionTables && virtualConnectionTables[0];
  return table ? table.downstreamFlowsConnection : null;
}

export type VirtualConnectionDownstreamFlowsQueryGridProps =
  GridSortProps<FlowsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function VirtualConnectionDownstreamFlowsQueryGrid(props: VirtualConnectionDownstreamFlowsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredFlowsLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamFlowsFilteredByTableQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as FlowSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForFlow}
    >
      {({ rowStore }) => <FlowsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamFlowsQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as FlowSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForFlow}
    >
      {({ rowStore }) => <FlowsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  );
}
